import isEmpty from 'lodash/isEmpty';
import parseInt from 'lodash/parseInt';
import {
  DATA_MIDDLE_CHART_ID,
  DATA_TOP_CHART_ID,
} from '../../../common/constants';
import { updateMultipleSearchParams } from '../../../common/utils';
import { DataChartEvent } from '../../../reducers/types/astronautEventType';
import { ChartDataItem } from '../../../reducers/types/dataChartTypes';

export interface ChartMeasurementsConfig {
  LINE_CHART_HEIGHT_IN_PX: number;
  STATUS_CHART_HEIGHT_IN_PX: number;
  LINE_CHARTS_PER_ZONE: Record<string, number>;
  STATUS_CHARTS_PER_ZONE: Record<string, number>;
  CONTAINER_HEIGHT_IN_PX: Record<string, number>;
}

export function getChartMeasurementsByChartData(
  chartsData: Record<string, ChartDataItem[]>,
  heightInPixels: number,
): ChartMeasurementsConfig | null {
  if (!isEmpty(chartsData)) {
    const numberOfLineChartsPerZone: Record<string, number> = {
      [DATA_TOP_CHART_ID]: 0,
      [DATA_MIDDLE_CHART_ID]: 0,
    };
    const numberOfStatusChartsPerZone: Record<string, number> = {
      [DATA_TOP_CHART_ID]: 0,
      [DATA_MIDDLE_CHART_ID]: 0,
    };
    Object.keys(chartsData).forEach((currentChartZone) => {
      chartsData[currentChartZone].forEach((currentGraph) => {
        if (currentGraph.type === 'line' || currentGraph.type === 'spline') {
          numberOfLineChartsPerZone[currentChartZone] = 1;
        }
        if (currentGraph.type === 'status') {
          numberOfStatusChartsPerZone[currentChartZone]++;
        }
      });
    });
    const numberOfLineCharts =
      numberOfLineChartsPerZone[DATA_TOP_CHART_ID] +
      numberOfLineChartsPerZone[DATA_MIDDLE_CHART_ID];
    const lineChartHeightRatio = 0.3;

    const numberOfStatusCharts =
      numberOfStatusChartsPerZone[DATA_TOP_CHART_ID] +
      numberOfStatusChartsPerZone[DATA_MIDDLE_CHART_ID];
    const statusChartHeightRatio =
      numberOfStatusCharts !== 0
        ? Math.max(
            (1 - lineChartHeightRatio * numberOfLineCharts) /
              numberOfStatusCharts,
            0.12,
          )
        : 0;

    const lineChartHeightInPx = parseInt(
      (lineChartHeightRatio * heightInPixels).toString(),
    );
    const statusChartHeightInPx = Math.min(
      parseInt((statusChartHeightRatio * heightInPixels).toString()),
      40,
    );

    const measurementsConfig: ChartMeasurementsConfig = {
      LINE_CHART_HEIGHT_IN_PX: lineChartHeightInPx,
      STATUS_CHART_HEIGHT_IN_PX: statusChartHeightInPx,
      LINE_CHARTS_PER_ZONE: {},
      STATUS_CHARTS_PER_ZONE: {},
      CONTAINER_HEIGHT_IN_PX: {},
    };

    measurementsConfig.LINE_CHARTS_PER_ZONE[DATA_TOP_CHART_ID] =
      numberOfLineChartsPerZone[DATA_TOP_CHART_ID];
    measurementsConfig.LINE_CHARTS_PER_ZONE[DATA_MIDDLE_CHART_ID] =
      numberOfLineChartsPerZone[DATA_MIDDLE_CHART_ID];

    measurementsConfig.STATUS_CHARTS_PER_ZONE[DATA_TOP_CHART_ID] =
      numberOfStatusChartsPerZone[DATA_TOP_CHART_ID];
    measurementsConfig.STATUS_CHARTS_PER_ZONE[DATA_MIDDLE_CHART_ID] =
      numberOfStatusChartsPerZone[DATA_MIDDLE_CHART_ID];

    measurementsConfig.CONTAINER_HEIGHT_IN_PX[DATA_TOP_CHART_ID] =
      numberOfLineChartsPerZone[DATA_TOP_CHART_ID] * lineChartHeightInPx;
    measurementsConfig.CONTAINER_HEIGHT_IN_PX[DATA_MIDDLE_CHART_ID] =
      numberOfLineChartsPerZone[DATA_MIDDLE_CHART_ID] * lineChartHeightInPx;

    return measurementsConfig;
  }

  return null;
}

export const formatAstronautEvents = (events: DataChartEvent[]) => {
  const eventsDataDictionary = {};

  events.forEach((currentEventData) => {
    const currentEventX =
      currentEventData && currentEventData.x
        ? new Date(currentEventData.x)
        : currentEventData.x;
    const currentEventLevel = currentEventData.level;
    const currentEventIcon = currentEventData.iconClass;
    const currentEventColor = currentEventData.color;
    if (!eventsDataDictionary[currentEventLevel]) {
      eventsDataDictionary[currentEventLevel] = {
        itemCSSClass: `bb-events-chart-${currentEventLevel}`,
        items: [],
        isSelected: true,
      };
    }
    eventsDataDictionary[currentEventLevel].color = currentEventColor;
    eventsDataDictionary[currentEventLevel].iconText = currentEventIcon;
    eventsDataDictionary[currentEventLevel].items.push({
      ...currentEventData,
      x: currentEventX,
    });
    Object.keys(eventsDataDictionary).forEach((currentKey) => {
      if (currentKey.toLowerCase() === 'warning') {
        eventsDataDictionary[currentKey].isSelected = false;
      }
    });
  });

  return eventsDataDictionary;
};

const BASE_WINDOW_HEIGTH = 852;
// Ratios based on window with 1920px width, 1080px height (innerHeight = 852)
const measurementsRatios = {
  COMMON_HEIGHT: 10 / BASE_WINDOW_HEIGTH,
  ATTACHING_EVENTS_BASELINE_Y: 0,
  ATTACHING_EVENTS_HEIGHT: 32 / BASE_WINDOW_HEIGTH,
  MILKING_SUBACTITIVITES_HEIGHT: 10 / BASE_WINDOW_HEIGTH,
  MILKING_STATE_HEIGHT: 10 / BASE_WINDOW_HEIGTH,
  ROBOT_STATE_HEIGHT: 20 / BASE_WINDOW_HEIGTH,
  CLEANING_STAGE_BASELINE_Y: 0,
  // By default at 1920/1080 (852 innerHeight) line chart height should be 40px
  // but there is a default 12px padding from the library
  // so we add those 12px to the height - thus 40 + 12 = 52
  LINE_CHART_HEIGHT: 52 / BASE_WINDOW_HEIGTH,
};

const getResponsiveMeasurementsConfig = () => {
  const { innerHeight } = window;
  const responsiveMeasurementsConfig = Object.keys(measurementsRatios).reduce(
    (returnObject, ratiosObjectKey) => {
      if (measurementsRatios[ratiosObjectKey] === 0) {
        returnObject[ratiosObjectKey] = 0;
      } else {
        returnObject[ratiosObjectKey] = Math.round(
          measurementsRatios[ratiosObjectKey] * innerHeight,
        );
      }

      return returnObject;
    },
    {} as typeof measurementsRatios,
  );

  return responsiveMeasurementsConfig;
};

export function getCompositeStatesChartMeasurementsBasedOnChartHeight() {
  const responsiveMeasurementConfig = getResponsiveMeasurementsConfig();

  const MILKING_SUBACTITIVITES_BASELINE_Y =
    responsiveMeasurementConfig.ATTACHING_EVENTS_BASELINE_Y +
    responsiveMeasurementConfig.ATTACHING_EVENTS_HEIGHT -
    responsiveMeasurementConfig.MILKING_SUBACTITIVITES_HEIGHT;
  const MILKING_STATE_BASELINE_Y =
    responsiveMeasurementConfig.ATTACHING_EVENTS_BASELINE_Y +
    responsiveMeasurementConfig.ATTACHING_EVENTS_HEIGHT;
  const ROBOT_STATE_BASELINE_Y = MILKING_SUBACTITIVITES_BASELINE_Y;

  const OVERALL_CLEANING_BASELINE_Y =
    responsiveMeasurementConfig.CLEANING_STAGE_BASELINE_Y +
    responsiveMeasurementConfig.COMMON_HEIGHT;

  const LINE_CHART_OFFSET_BOTTOM = responsiveMeasurementConfig.COMMON_HEIGHT;

  return {
    ...responsiveMeasurementConfig,
    MILKING_SUBACTITIVITES_BASELINE_Y,
    ROBOT_STATE_BASELINE_Y,
    MILKING_STATE_BASELINE_Y,
    OVERALL_CLEANING_BASELINE_Y,
    LINE_CHART_OFFSET_BOTTOM,
  };
}

export const eraseSelectedActivityFromUrl = () => {
  updateMultipleSearchParams(
    [
      { key: 'selectedStartDate', value: '' },
      { key: 'selectedEndDate', value: '' },
      { key: 'mouseX', value: '' },
      { key: 'mouseY', value: '' },
      { key: 'selectedAlarms', value: '' },
      { key: 'selectedName', value: '' },
    ],
    true,
  );
};
